import React from 'react';
import { X, CheckCircle, Users, XCircle, Mail } from 'lucide-react';

interface Driver {
    id: string;
    firstName: string;
    lastName: string;
    licenceNumber: string;
    reason: string;
}

interface ConsentSendResultsModalProps {
    isOpen: boolean;
    onClose: () => void;
    emailsSent: number;
    totalImported: number;
    driversWithoutEmail: Driver[];
}

const ConsentSendResultsModal: React.FC<ConsentSendResultsModalProps> = ({
    isOpen,
    onClose,
    emailsSent,
    totalImported,
    driversWithoutEmail
}) => {
    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] flex flex-col">
                {/* Header */}
                <div className="flex justify-between items-center p-6 border-b border-gray-200">
                    <div className="flex items-center space-x-3">
                        <div className="p-2 rounded-lg" style={{ backgroundColor: '#69BE2820' }}>
                            <Mail className="w-5 h-5" style={{ color: '#69BE28' }} />
                        </div>
                        <div>
                            <h3 className="text-lg font-semibold text-gray-900">Consent Forms Sent</h3>
                            <p className="text-sm text-gray-600">
                                Sent {emailsSent} email{emailsSent !== 1 ? 's' : ''} to {totalImported} imported driver{totalImported !== 1 ? 's' : ''}
                            </p>
                        </div>
                    </div>
                    <button
                        onClick={onClose}
                        className="text-gray-400 hover:text-gray-600 transition-colors"
                    >
                        <X className="w-5 h-5" />
                    </button>
                </div>

                {/* Statistics */}
                <div className="p-6 border-b border-gray-200">
                    <div className="grid grid-cols-3 gap-4">
                        <div className="flex items-center space-x-3 p-3 bg-green-50 rounded-lg">
                            <CheckCircle className="w-8 h-8" style={{ color: '#69BE28' }} />
                            <div>
                                <p className="text-sm text-gray-600">Emails Sent</p>
                                <p className="text-2xl font-bold" style={{ color: '#69BE28' }}>{emailsSent}</p>
                            </div>
                        </div>

                        <div className="flex items-center space-x-3 p-3 bg-blue-50 rounded-lg">
                            <Users className="w-8 h-8 text-blue-600" />
                            <div>
                                <p className="text-sm text-gray-600">Total Imported</p>
                                <p className="text-2xl font-bold text-blue-600">{totalImported}</p>
                            </div>
                        </div>

                        <div className="flex items-center space-x-3 p-3 bg-orange-50 rounded-lg">
                            <XCircle className="w-8 h-8 text-orange-600" />
                            <div>
                                <p className="text-sm text-gray-600">Not Sent</p>
                                <p className="text-2xl font-bold text-orange-600">{driversWithoutEmail.length}</p>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Drivers Without Email List */}
                <div className="flex-1 overflow-y-auto p-6">
                    <h4 className="text-sm font-semibold text-gray-900 mb-3">
                        Drivers Not Sent
                    </h4>

                    {driversWithoutEmail.length === 0 ? (
                        <div className="text-center py-8">
                            <CheckCircle className="w-12 h-12 mx-auto mb-3" style={{ color: '#69BE28' }} />
                            <p className="text-gray-600 font-medium">All drivers received consent forms!</p>
                            <p className="text-sm text-gray-500 mt-1">
                                Every driver was sent a consent request email successfully.
                            </p>
                        </div>
                    ) : (
                        <div className="border border-gray-200 rounded-lg overflow-hidden">
                            <table className="min-w-full divide-y divide-gray-200">
                                <thead className="bg-gray-50">
                                    <tr>
                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Name
                                        </th>
                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Licence Number
                                        </th>
                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Reason
                                        </th>
                                    </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-gray-200">
                                    {driversWithoutEmail.map((driver) => (
                                        <tr key={driver.id} className="hover:bg-gray-50">
                                            <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                                                {driver.firstName} {driver.lastName}
                                            </td>
                                            <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500 font-mono">
                                                {driver.licenceNumber}
                                            </td>
                                            <td className="px-4 py-3 whitespace-nowrap text-sm">
                                                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
                                                    {driver.reason}
                                                </span>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className="flex justify-end p-6 border-t border-gray-200">
                    <button
                        type="button"
                        onClick={onClose}
                        className="px-4 py-2 text-white rounded-md hover:opacity-90 transition-opacity"
                        style={{ backgroundColor: '#69BE28' }}
                    >
                        Close
                    </button>
                </div>
            </div>
        </div>
    );
};

export default ConsentSendResultsModal;
